import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { en } from '../data/content.en';
import { es } from '../data/content.es';
import type { Content, Language } from '../types/content';
const STORAGE_KEY = 'sca-language';
const dictionaries: Record<Language, Content> = {
  en,
  es
};
type LanguageContextValue = {
  language: Language | null;
  hasChosen: boolean;
  t: Content;
  setLanguage: (language: Language) => void;
};
const LanguageContext = createContext<LanguageContextValue | null>(null);
function readStoredLanguage(): Language | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'en' || stored === 'es' ? stored : null;
  } catch {
    return null;
  }
}
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguageState] = useState<Language | null>(() => readStoredLanguage());
  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable — language still applies for this session */
    }
  }, []);
  useEffect(() => {
    document.documentElement.lang = language ?? 'en';
    document.title = dictionaries[language ?? 'en'].documentTitle;
  }, [language]);
  const value = useMemo<LanguageContextValue>(() => ({
    language,
    hasChosen: language !== null,
    t: dictionaries[language ?? 'en'],
    setLanguage
  }), [language, setLanguage]);
  return <LanguageContext.Provider value={value} data-id="element-359" data-mp="2u5opyj0qljv8">{children}</LanguageContext.Provider>;
}
export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}